import React from 'react';
import { Users, MessageCircle, Calendar, Star, Search } from 'lucide-react';
import { useLocalization } from '../../contexts/LocalizationContext';

interface CommunityHeaderProps {
  activeTab: 'forums' | 'events' | 'network';
  onTabChange: (tab: 'forums' | 'events' | 'network') => void;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  onSearchSubmit: () => void;
}

const CommunityHeader: React.FC<CommunityHeaderProps> = ({
  activeTab,
  onTabChange,
  searchQuery,
  onSearchChange,
  onSearchSubmit
}) => {
  const { language, isRTL, strings } = useLocalization();

  const tabs = [
    {
      id: 'forums' as const,
      label: strings.community?.forums || 'Forums',
      icon: MessageCircle,
      description: strings.community?.forumsDescription || 'Legal discussions'
    },
    {
      id: 'events' as const,
      label: strings.community?.events || 'Events',
      icon: Calendar,
      description: strings.community?.eventsDescription || 'Seminars & workshops'
    },
    {
      id: 'network' as const,
      label: strings.community?.network || 'Network',
      icon: Users,
      description: strings.community?.networkDescription || 'Connect with lawyers'
    }
  ];

  return (
    <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 mb-8">
      {/* Main Header */}
      <div className="p-8 text-center border-b border-slate-200 dark:border-slate-700">
        <div className="flex justify-center mb-6">
          <div className="p-4 bg-primary/10 rounded-full">
            <Users className="w-12 h-12 text-primary" />
          </div>
        </div>
        <h1 className="text-4xl font-bold text-slate-800 dark:text-white mb-4">
          {strings.community?.title || 'Community'}
        </h1>
        <p className="text-xl text-slate-600 dark:text-slate-300 max-w-2xl mx-auto">
          {strings.community?.subtitle || 'Join our community of lawyers, share experiences, and build a strong professional network'}
        </p>
      </div>

      {/* Search Bar */}
      <div className="p-6 border-b border-slate-200 dark:border-slate-700">
        <div className="max-w-2xl mx-auto">
          <div className="relative">
            <Search className={`absolute ${isRTL ? 'right-3' : 'left-3'} top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5`} />
            <input
              type="text"
              placeholder={strings.community?.searchPlaceholder || 'Search community...'}
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && onSearchSubmit()}
              className={`w-full ${isRTL ? 'pr-12 pl-4' : 'pl-12 pr-4'} py-3 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent`}
            />
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="p-6">
        <div className="max-w-4xl mx-auto">
          <div className={`grid grid-cols-1 md:grid-cols-3 gap-4 ${isRTL ? 'md:grid-cols-3' : ''}`}>
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              
              return (
                <button
                  key={tab.id}
                  onClick={() => onTabChange(tab.id)}
                  className={`p-6 rounded-xl text-left transition-all duration-200 ${
                    isActive
                      ? 'bg-primary text-white shadow-lg'
                      : 'bg-slate-50 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-600'
                  } ${isRTL ? 'text-right' : 'text-left'}`}
                >
                  <div className={`flex items-center gap-3 mb-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <Icon className={`w-6 h-6 ${isActive ? 'text-white' : 'text-primary'}`} />
                    <h3 className="text-lg font-semibold">{tab.label}</h3>
                  </div>
                  <p className={`text-sm ${isActive ? 'text-white/80' : 'text-slate-500 dark:text-slate-400'}`}>
                    {tab.description}
                  </p>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommunityHeader;
